/**
 * Los modelos de iPhone que existen, en dos preguntas.
 *
 * POR QUE NO ES UN CAMPO DE TEXTO
 * -------------------------------
 * El alta pedía el modelo escrito: "16 PRO", "17", "15 PLUS". Funciona hasta
 * que alguien escribe "16 pro max", "16 PROMAX" o "16 Pro Max", y entonces son
 * cuatro productos distintos para la base, cuatro filas en la lista y cuatro
 * respuestas al buscar. Nada en el sistema lo puede corregir después: el
 * modelo no se guarda en una columna, vive adentro del nombre.
 *
 * Los modelos que Apple hizo son una lista cerrada y ya la tenemos —es la
 * misma tabla que dice en qué colores viene cada uno—, así que la pregunta se
 * contesta eligiendo y no escribiendo.
 *
 * SE PREGUNTA EN DOS PASOS PORQUE ASI SE PIENSA
 * ---------------------------------------------
 * Primero la generación ("16") y después la variante ("PRO MAX"). Es como se
 * pregunta en el mostrador —"¿el 16? ¿el Pro Max?"— y además parte una lista
 * de cuarenta y pico de modelos en dos de diez y de cinco, que entran en la
 * pantalla de un teléfono sin scrollear.
 */
import { COLORES_POR_MODELO } from "@/components/catalogo/colores";

/**
 * "16 PRO MAX" → generación "16", variante "PRO MAX". "16E" → "16" + "E".
 *
 * Es público porque también se usa al revés: abrir la ficha de un equipo ya
 * cargado necesita volver del modelo guardado a las dos respuestas con las que
 * se eligió, para dejar los chips donde corresponde.
 */
export function partirModelo(clave: string): { generacion: string; variante: string } {
  const m = /^(\d+)\s*(.*)$/.exec(clave);
  if (!m) return { generacion: clave, variante: "" };
  return { generacion: m[1] ?? "", variante: (m[2] ?? "").trim() };
}

/** base < e < Plus < Pro < Pro Max: el orden del lineup, no el alfabético. */
function rango(variante: string): number {
  if (variante === "PRO MAX") return 4;
  if (variante === "PRO") return 3;
  if (variante === "PLUS") return 2;
  if (variante === "E") return 1;
  return 0;
}

const MODELOS = Object.keys(COLORES_POR_MODELO).map(partirModelo);

/**
 * Donde entra un modelo que no lleva numero de generacion.
 *
 * El lineup se recita por numero, de lo nuevo a lo viejo, y los que no tienen
 * numero hay que ubicarlos a mano. No van todos juntos al fondo: el Duo es el
 * telefono del año —el mas caro y el que hay que tener a mano— y mandarlo
 * abajo del 13 porque no se llama «19» seria ordenar por como se escribe el
 * nombre y no por lo que se vende. El Air queda detras del lineup numerado, y
 * el SE al fondo, que es donde corresponde: es museo.
 *
 * El numero es ficticio y solo sirve para ordenar: 999 va arriba de todo, y
 * -1 abajo de todo.
 */
const LUGAR_SIN_NUMERO: Record<string, number> = { DUO: 999, AIR: -1, SE: -2 };

/** El numero con el que se ordena una generacion, tenga numero o no. */
function ordenDeGeneracion(generacion: string): number {
  const n = Number(generacion);
  return Number.isNaN(n) ? (LUGAR_SIN_NUMERO[generacion] ?? -3) : n;
}

/**
 * Las generaciones, las nuevas primero. El Duo arriba de todo; Air y SE al
 * final. Ver `LUGAR_SIN_NUMERO`.
 */
export const GENERACIONES: ReadonlyArray<string> = [
  ...new Set(MODELOS.map((m) => m.generacion)),
].sort((a, b) => ordenDeGeneracion(b) - ordenDeGeneracion(a));

/**
 * Las variantes de esa generación, de menor a mayor. La cadena vacía es el
 * modelo base —el "16" a secas— y siempre existe si Apple lo hizo.
 */
export function variantesDe(generacion: string): string[] {
  return MODELOS.filter((m) => m.generacion === generacion)
    .map((m) => m.variante)
    .sort((a, b) => rango(a) - rango(b));
}

/** Las dos respuestas, juntas: ("16", "PRO MAX") → "16 PRO MAX". */
export function modeloDe(generacion: string, variante: string): string {
  if (!generacion) return "";
  if (!variante) return generacion;
  // El 16e se escribe pegado; el resto va separado.
  return variante === "E" ? `${generacion}E` : `${generacion} ${variante}`;
}

/** Como se lee una variante en un chip. La base no tiene nombre propio. */
export function etiquetaVariante(variante: string): string {
  if (!variante) return "Base";
  if (variante === "E") return "e";
  return variante.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Los modelos del lineup, de la generación que se pida en adelante.
 *
 * POR QUE LA PANTALLA MUESTRA MODELOS QUE NO TENEMOS
 * --------------------------------------------------
 * Porque este negocio vende A PEDIDO: lo que existe es lo que se puede
 * ofrecer, y el catálogo cargado es apenas la parte que ya tiene precio. Hasta
 * ahora, que faltara el 16 Pro no se veía en ningún lado — se descubría cuando
 * alguien llamaba a preguntar y no había qué contestarle.
 *
 * Con el lineup de esqueleto, el hueco se ve: la fila está, dice «sin cargar»,
 * y muestra en qué colores viene. Llenarla es cargarle el precio.
 *
 * El corte por generación existe para que la lista no se vuelva un museo: del
 * 13 para arriba es lo que rota. `AIR` y `DUO` entran porque son de los dos
 * años en curso y no llevan número; `SE` no, que es el museo.
 */
const SIN_NUMERO_QUE_ROTAN = new Set(["AIR", "DUO"]);

export function modelosDesde(generacionMinima: number): string[] {
  return MODELOS.filter((m) => {
    const n = Number(m.generacion);
    return Number.isNaN(n) ? SIN_NUMERO_QUE_ROTAN.has(m.generacion) : n >= generacionMinima;
  }).map((m) => modeloDe(m.generacion, m.variante));
}

/** El lugar de una generación en el lineup. Público para poder probarlo. */
export { ordenDeGeneracion };

/** Los modelos, de lo más nuevo a lo más viejo, como se recita el lineup. */
export function ordenarModelos(claves: ReadonlyArray<string>): string[] {
  return [...claves].sort((a, b) => {
    const pa = partirModelo(a);
    const pb = partirModelo(b);
    const oa = ordenDeGeneracion(pa.generacion);
    const ob = ordenDeGeneracion(pb.generacion);
    if (oa !== ob) return ob - oa;
    // Dos generaciones sin número que caen en el mismo lugar: por nombre, para
    // que el orden sea estable y no dependa de cómo llegó la lista.
    if (pa.generacion !== pb.generacion) return pa.generacion.localeCompare(pb.generacion, "es");
    return rango(pb.variante) - rango(pa.variante);
  });
}
